type PurchasePricingValue = string | number | null;

interface PurchasePricingRateBlockServerData {
  rate?: PurchasePricingValue;
  discount?: PurchasePricingValue;
  net_cost?: PurchasePricingValue;
  qty?: PurchasePricingValue;
}

interface PurchasePricingVendorServerData {
  vendor_name?: string;
  created_by?: string;
  user_name?: string;
  per_case?: PurchasePricingRateBlockServerData;
  per_item?: PurchasePricingRateBlockServerData;
}

interface PurchasePricingStoreServerData {
  store_id?: string;
  store_name?: string;
  vendors?: PurchasePricingVendorServerData[];
}

interface PurchasePricingHistoryServerData {
  date?: string;
  stores?: PurchasePricingStoreServerData[];
}

interface PurchasePricingResponseServerData {
  selected_stores?: Array<{
    store_id?: string;
    store_name?: string;
  }>;
  purchase_history?: PurchasePricingHistoryServerData[];
}

interface PurchasePricingSelectedStore {
  store_id: string;
  store_name: string;
}

interface PurchasePricingRateBlock {
  rate: PurchasePricingValue;
  discount: PurchasePricingValue;
  net_cost: PurchasePricingValue;
  qty: PurchasePricingValue;
}

interface PurchasePricingVendor {
  vendor_name: string;
  created_by: string;
  user_name: string;
  per_case: PurchasePricingRateBlock;
  per_item: PurchasePricingRateBlock;
}

interface PurchasePricingStore {
  store_id: string;
  store_name: string;
  vendors: PurchasePricingVendor[];
}

interface PurchasePricingHistory {
  date: string;
  stores: PurchasePricingStore[];
}

class ItemPurchasePricingModel {
  private _serverData: PurchasePricingResponseServerData;

  constructor(serverData: PurchasePricingResponseServerData = {}) {
    this._serverData = serverData;
  }

  get selected_stores(): PurchasePricingSelectedStore[] {
    if (!Array.isArray(this._serverData?.selected_stores)) {
      return [];
    }

    return this._serverData.selected_stores
      .map((store) => ({
        store_id: String(store?.store_id || '').trim(),
        store_name: String(store?.store_name || '').trim(),
      }))
      .filter((store) => store.store_id && store.store_name);
  }

  get purchase_history(): PurchasePricingHistory[] {
    if (!Array.isArray(this._serverData?.purchase_history)) {
      return [];
    }

    return this._serverData.purchase_history.map((historyEntry) => ({
      date: String(historyEntry?.date || '').trim(),
      stores: Array.isArray(historyEntry?.stores)
        ? historyEntry.stores
            .map((storeEntry) => ({
              store_id: String(storeEntry?.store_id || '').trim(),
              store_name: String(storeEntry?.store_name || '').trim(),
              vendors: Array.isArray(storeEntry?.vendors)
                ? storeEntry.vendors.map((vendorEntry) => ({
                    vendor_name: String(vendorEntry?.vendor_name || '').trim(),
                    created_by: String(vendorEntry?.created_by || '').trim(),
                    user_name: String(vendorEntry?.user_name || '').trim(),
                    per_case: {
                      rate: vendorEntry?.per_case?.rate ?? null,
                      discount: vendorEntry?.per_case?.discount ?? null,
                      net_cost: vendorEntry?.per_case?.net_cost ?? null,
                      qty: vendorEntry?.per_case?.qty ?? null,
                    },
                    per_item: {
                      rate: vendorEntry?.per_item?.rate ?? null,
                      discount: vendorEntry?.per_item?.discount ?? null,
                      net_cost: vendorEntry?.per_item?.net_cost ?? null,
                      qty: vendorEntry?.per_item?.qty ?? null,
                    },
                  }))
                : [],
            }))
            .filter((storeEntry) => storeEntry.store_id && storeEntry.store_name)
        : [],
    }));
  }

  static fromServerData(serverData: PurchasePricingResponseServerData) {
    return new ItemPurchasePricingModel(serverData);
  }

  static createEmpty() {
    return new ItemPurchasePricingModel({
      selected_stores: [],
      purchase_history: [],
    });
  }
}

export default ItemPurchasePricingModel;
